import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiCheckCircle, FiShield, FiAward, FiArrowRight } from 'react-icons/fi';
import { useSiteContent } from '../context/SiteContentContext';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const { siteContent } = useSiteContent();
  const aboutCms = siteContent?.about || {};

  const badge = aboutCms.badge || "About RojrzTech";
  const heading = aboutCms.heading || "Empowering Your Digital Journey";
  const desc1 = aboutCms.desc1 || "We are a fully registered LLC and a premier software and digital marketing agency. Our mission is to transform your vision into reality through cutting-edge technology, innovative design, and data-driven marketing strategies.";
  const desc2 = aboutCms.desc2 || "Whether you need a powerful web application, a seamless mobile app, or a complete digital brand overhaul, we have the expertise to deliver scalable solutions that drive real business growth.";
  const image = aboutCms.image || "/about.jpeg";
  const sectionRef = useRef(null);
  const leftColRef = useRef(null);
  const rightColRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Left Column Animation (Image & Cards)
      gsap.fromTo(
        leftColRef.current,
        { x: -50, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
          },
        }
      );

      // Right Column Animation (Content Stagger)
      gsap.fromTo(
        rightColRef.current.children,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <section
        id="about"
        ref={sectionRef}
        className="w-full py-8 md:py-20 bg-transparent"
      >
        {/* Top Fade Mask */}
        <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-black to-transparent pointer-events-none z-0" />

        {/* Bottom Fade Mask */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black to-transparent pointer-events-none z-0" />

        <div className="relative z-10 container-custom">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-12 items-center">

            {/* LEFT COLUMN: Image & Floating Cards */}
            <div ref={leftColRef} className="relative">
              {/* Main Image */}
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] group z-10">
                <img
                  src="/about.jpeg"
                  alt="RojrzTech Team"
                  className="w-full h-auto object-cover aspect-square md:aspect-[4/5] lg:aspect-square transform transition-transform duration-700 group-hover:scale-105 opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#F2B135]/30 to-transparent mix-blend-overlay pointer-events-none" />
              </div>

              {/* Glowing aura */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-[#F2B135]/20 blur-[100px] rounded-full z-0 pointer-events-none" />

              {/* Floating Card 1: Top Left (LLC Certified) */}
              <div className="absolute -top-8 -left-4 lg:-left-12 z-20 animate-float-slow">
                <div className="bg-black/80 backdrop-blur-xl border border-[#F2B135]/50 rounded-2xl p-5 flex items-center gap-4 shadow-[0_10px_40px_rgba(242,177,53,0.2)]">
                  <div className="w-14 h-14 rounded-full bg-[#F2B135]/20 border border-[#F2B135] flex items-center justify-center text-[#F2B135]">
                    <FiShield size={28} />
                  </div>
                  <div>
                    <div className="text-white font-black text-xl leading-none mb-1">Registered</div>
                    <div className="text-[#F2B135] text-sm font-bold uppercase tracking-widest">LLC Company</div>
                  </div>
                </div>
              </div>

              {/* Floating Card 2: Bottom Right (Experience) */}
              <div className="absolute -bottom-8 -right-4 lg:-right-8 z-20 animate-float-fast">
                <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5 flex items-center gap-4 shadow-2xl">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F2B135] to-[#d4941e] flex items-center justify-center text-black">
                    <FiAward size={24} />
                  </div>
                  <div>
                    <div className="text-white font-bold text-lg">Top Rated</div>
                    <div className="text-white/70 text-xs font-semibold">Digital Agency</div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Content */}
            <div ref={rightColRef} className="flex flex-col items-start text-left lg:pl-8">

              <div className="inline-flex items-center gap-2 bg-[#F2B135]/10 border border-[#F2B135]/20 px-4 py-1.5 rounded-full mb-6">
                <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
                <span className="text-xs font-black text-[#F2B135] uppercase tracking-[0.2em]">
                  {badge}
                </span>
              </div>

              <h2
                className="text-3xl md:text-5xl font-black leading-[1.2] text-white mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {heading}
              </h2>

              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6 font-medium">
                {desc1}
              </p>

              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8 font-medium">
                {desc2}
              </p>

              {/* Checkmarks Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full mb-10">
                {[
                  'Custom Web Development',
                  'Mobile App Creation',
                  'E-commerce & Shopify',
                  'UI/UX Design & Branding',
                  'SEO & Digital Marketing',
                  'IT Staff Augmentation'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 text-white/90 font-semibold text-sm">
                    <div className="min-w-[24px] h-6 rounded-full bg-[#F2B135]/20 flex items-center justify-center text-[#F2B135]">
                      <FiCheckCircle size={14} />
                    </div>
                    {item}
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <a href="#services" className="btn-animated-gold">
                Explore Our Services <FiArrowRight size={18} />
              </a>

            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default About;